import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { getAllCars } from '@/modules/car/store/selectors';
import { loadDriverById } from '@/modules/driver/actions/async/load-driver-by-id';
import { updateDriverInfo } from '@/modules/driver/actions/async/updateDriver';
import { TUpdateCurrentVehicleForm } from '@/modules/driver/model/driver';
import { getCurrentDriver } from '@/modules/driver/store/selectors';
import { AppDispatch } from '@/root-store';
import { useForm } from '@/shared/view/Form';

import { EditCurrentVehicleView } from './view';

interface IProps {
  onNavigateBack: (uri: string) => void;
}

const ROUTE = '/chauffeurs/:driverId/edit-vehicle';

const EditCurrentVehicle = ({ onNavigateBack }: IProps) => {
  const driverId = useRouteMatch<{ driverId: string }>(ROUTE)?.params?.driverId;

  const dispatch: AppDispatch = useDispatch();

  const driver = useSelector(getCurrentDriver);
  const vehicles = useSelector(getAllCars);

  const navigateBack = useCallback(
    () => onNavigateBack(`/chauffeurs/${driverId}`),
    [driverId, onNavigateBack],
  );

  const postForm = useCallback(
    async (formValues: Parameters<typeof updateDriverInfo>[0]) => {
      const { isSucceed, result = {} } = await dispatch(updateDriverInfo(formValues)).unwrap();

      if (isSucceed && !result.validationErrors && !result.baseErrors) {
        navigateBack();
      }

      return result;
    },
    [dispatch, navigateBack],
  );

  const {
    handleSubmit,
    form: {
      control,
      formState: { isSubmitting },
      resetField,
    },
  } = useForm<TUpdateCurrentVehicleForm>({
    onSubmit: postForm,
    defaultValues: {
      carId: driver?.carId,
    },
  });

  useEffect(() => {
    if (!driverId) {
      return;
    }

    dispatch(loadDriverById(driverId));
  }, [dispatch, driverId, resetField]);

  useEffect(() => {
    resetField('carId', { defaultValue: driver?.carId || '' });
  }, [resetField, driver?.carId]);

  return (
    <EditCurrentVehicleView
      control={control}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onNavigateBack={navigateBack}
      vehicles={vehicles}
    />
  );
};

export { EditCurrentVehicle, ROUTE };
